import React, { useMemo } from 'react';
import { FlockProfileDisplayTrait, getDisplayTraits } from './flock-profile-labels';
import { FlockProfileCardModel } from './flock-profile-types';

type Props = {
  model: FlockProfileCardModel;
};

type MobileTraitGroup = {
  groupLabel: string;
  color: string;
  traits: FlockProfileDisplayTrait[];
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const getPercentileLabel = (analysisName: string): string => {
  const trimmedAnalysisName = analysisName.trim();

  if (trimmedAnalysisName.length === 0) {
    return 'Percentile';
  }

  return `${trimmedAnalysisName} Percentile`;
};

const darkenHex = (hex: string, amount: number = 0.3): string => {
  const safeHex = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(safeHex)) return hex;

  const toChannel = (start: number) => {
    const value = parseInt(safeHex.slice(start, start + 2), 16);
    const darkened = Math.max(0, Math.min(255, Math.round(value * (1 - amount))));
    return darkened.toString(16).padStart(2, '0');
  };

  return `#${toChannel(0)}${toChannel(2)}${toChannel(4)}`;
};

const getMobileBarStyle = (percentile: number) => {
  const p = clamp(percentile, 0, 100);
  if (p < 50) return { left: '50%', width: `${Math.max(50 - p, 1)}%` };
  if (p > 50) return { left: `${100 - p}%`, width: `${Math.max(p - 50, 1)}%` };
  return { left: '50%', width: '1%' };
};

const hexToRgba = (hex: string, alpha: number): string => {
  const safeHex = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(safeHex)) return hex;
  const r = parseInt(safeHex.slice(0, 2), 16);
  const g = parseInt(safeHex.slice(2, 4), 16);
  const b = parseInt(safeHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getMobileBarAppearance = (percentile: number, color: string) => {
  const position = getMobileBarStyle(percentile);
  const p = clamp(percentile, 0, 100);
  const solid = hexToRgba(color, 1);
  const faded = hexToRgba(color, 0.2);

  if (p > 50) return { ...position, background: `linear-gradient(90deg, ${solid} 0%, ${faded} 100%)`, borderRadius: '6px 0 0 6px' };
  return { ...position, background: `linear-gradient(90deg, ${faded} 0%, ${solid} 100%)`, borderRadius: '0 6px 6px 0' };
};

const getMobileBarCircleAppearance = (percentile: number) => {
  const p = clamp(percentile, 0, 100);
  const circleDiameter = 13;
  if (p === 50) return { left: `calc(50% - 6.5px)` };
  return { left: `calc(${100 - p}% - ${circleDiameter - (circleDiameter * p / 100)}px)` };
};

const getAccuracyClassName = (label?: string): string => {
  switch (label?.trim().toLowerCase()) {
    case 'low':
      return 'sg-flock-profile-card__mobile-accuracy sg-flock-profile-card__mobile-accuracy--low';
    case 'medium':
      return 'sg-flock-profile-card__mobile-accuracy sg-flock-profile-card__mobile-accuracy--medium';
    case 'high':
      return 'sg-flock-profile-card__mobile-accuracy sg-flock-profile-card__mobile-accuracy--high';
    default:
      return 'sg-flock-profile-card__mobile-accuracy';
  }
};

export const FlockProfileChartMobile: React.FC<Props> = ({ model }) => {
  const groupedTraits = useMemo<MobileTraitGroup[]>(() => {
    const groupMap = new Map<string, MobileTraitGroup>();
    model.traits.forEach((trait, traitIndex) => {
      if (!groupMap.has(trait.groupLabel)) {
        groupMap.set(trait.groupLabel, { groupLabel: trait.groupLabel, color: trait.color || '#4b4f56', traits: [] });
      }
      groupMap.get(trait.groupLabel)?.traits.push({
        trait,
        traitIndex,
        displayLabel: trait.label,
      });
    });

    return Array.from(groupMap.values()).map((group) => ({
      ...group,
      traits: getDisplayTraits(group.traits.map(({ trait, traitIndex }) => ({ trait, traitIndex }))),
    }));
  }, [model.traits]);

  return (
    <>
      <section className="sg-flock-profile-card__mobile" aria-label="Flock profile mobile chart">
        {groupedTraits.map((group, groupIndex) => (
          <div key={`${group.groupLabel}-${groupIndex}`}>
            <div className="sg-flock-profile-card__mobile-group-heading" style={{ color: darkenHex(group.color) }}>
              {group.groupLabel}
            </div>
            {group.traits.map((displayTrait, index) => {
              const { trait } = displayTrait;
              const traitColour = trait.color || '#4b4f56';
              const barStyle = getMobileBarAppearance(trait.percentile, traitColour);
              const circleStyle = getMobileBarCircleAppearance(trait.percentile);
              const previousTrait = index > 0 ? group.traits[index - 1] : undefined;
              const showSubgroup = displayTrait.subgroupLabel && displayTrait.subgroupLabel !== previousTrait?.subgroupLabel;
              const codeStyle = displayTrait.subgroupLabel ? { color: darkenHex(group.color) } : undefined;

              return (
                <React.Fragment key={`${trait.code}-${groupIndex}-${index}`}>
                  {showSubgroup && (
                    <div className="sg-flock-profile-card__mobile-subgroup-heading" style={{ color: darkenHex(group.color) }}>
                      {displayTrait.subgroupLabel} ({displayTrait.subgroupCode})
                    </div>
                  )}
                  <div className="sg-flock-profile-card__mobile-trait">
                    <div className="sg-flock-profile-card__mobile-trait-header">
                      <div className="sg-flock-profile-card__mobile-label"><b style={codeStyle}>{trait.code}</b> {displayTrait.displayLabel}</div>
                      <div className="sg-flock-profile-card__mobile-meta">
                        <span>Value: {trait.value}</span>
                        <span>Percentile: {clamp(trait.percentile, 0, 100)}</span>
                        {trait.accuracyLabel && <span className={getAccuracyClassName(trait.accuracyLabel)}>Accuracy: {trait.accuracyLabel}</span>}
                      </div>
                    </div>
                    <div className="sg-flock-profile-card__mobile-bar-row">
                      <span className="sg-flock-profile-card__mobile-side-label">{trait.leftLabel}</span>
                      <div className="sg-flock-profile-card__mobile-bar-container">
                        <div className="sg-flock-profile-card__mobile-bar-circle" style={{ ...circleStyle, border: `1px solid ${darkenHex(traitColour)}` }} />
                        <div className="sg-flock-profile-card__mobile-bar" style={barStyle} />
                      </div>
                      <span className="sg-flock-profile-card__mobile-side-label">{trait.rightLabel}</span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </section>
      <div className="sg-flock-profile-card__mobile-axis-label">{getPercentileLabel(model.analysisName)}</div>
    </>
  );
};
